import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import type { ParkingSpotRejection } from '@/shared/api';
import { addDays } from './parkingOccupancyHelpers';
import { parkingChangeProblem } from './parkingProblemText';

export type ParkingPeriodMode = 'block' | 'guest';

export interface ParkingPeriodDraft {
  mode: ParkingPeriodMode;
  from: string;
  to: string;
  note: string;
  licensePlate: string;
  bookingId: string;
  search: string;
}

/** `today` ist der Tag in der Zeitzone des Objekts, nicht der UTC-Tag der Browseruhr. */
export const emptyParkingPeriodDraft = (
  mode: ParkingPeriodMode,
  today: string,
): ParkingPeriodDraft => ({
  mode,
  from: today,
  to: addDays(today, 1),
  note: '',
  licensePlate: '',
  bookingId: '',
  search: '',
});

/**
 * Entwurf und Absenden eines Stellplatz-Zeitraums für die **ausgewählten** Plätze — als kostenlose
 * Sperre oder zugeordnet zu einer bestehenden Buchung. Zwei Endpunkte, ein Formular: der
 * Unterschied liegt im Modus, nicht in einer zweiten Maske.
 *
 * Teilerfolg ist der Normalfall, nicht die Ausnahme: die Überschneidung entscheidet die Datenbank
 * je Platz. Deshalb bleibt der Bericht stehen, statt in einem Toast zu verschwinden.
 */
export function useParkingPeriodDraft(
  propertyGroupId: string,
  spotIds: string[],
  today: string,
  onDone: () => void,
) {
  const api = useApi();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ParkingPeriodDraft>(() =>
    emptyParkingPeriodDraft('block', today),
  );
  const [rejected, setRejected] = useState<ParkingSpotRejection[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const patch = (value: Partial<ParkingPeriodDraft>) =>
    setDraft((current) => ({ ...current, ...value }));

  const create = useMutation({
    // Offline sofort scheitern – ein pausierter Auftrag liefe später unbemerkt los.
    networkMode: 'always',
    onMutate: () => setProblem(null),
    mutationFn: async () => {
      // Nach einem Teilerfolg zielt der nächste Versuch nur noch auf die Plätze, die abgelehnt
      // wurden. Sonst legt ein zweiter Klick — etwa mit korrigiertem Datum — auf den bereits
      // erledigten Plätzen eine zweite Zeile an, und der Toast meldet trotzdem „alle erledigt".
      const ziel = rejected.length > 0 ? rejected.map((row) => row.spotId) : spotIds;
      const period = { spotIds: ziel, from: draft.from, to: draft.to };
      const plate = draft.licensePlate.trim() || null;
      return draft.mode === 'block'
        ? api.parking.createBlock(propertyGroupId, {
            ...period,
            note: draft.note.trim() || null,
            licensePlate: plate,
          })
        : api.parking.createMappedReservation(propertyGroupId, {
            ...period,
            bookingId: draft.bookingId,
            licensePlate: plate,
          });
    },
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['parking-occupancy', propertyGroupId] });
      setRejected(result.rejected);
      const verb = draft.mode === 'block' ? 'gesperrt' : 'zugeordnet';
      if (result.created.length === 0) {
        toast.error(`Kein Stellplatz ${verb} — siehe Gründe.`);
        return;
      }
      if (result.rejected.length > 0) {
        // „Zwei von drei" ist eine andere Aussage als „erledigt" — der Dialog bleibt offen,
        // damit der Mitarbeiter liest, welcher Platz fehlt und warum.
        toast.warning(
          `${result.created.length} von ${result.created.length + result.rejected.length} ${verb}.`,
        );
        return;
      }
      toast.success(
        result.created.length === 1
          ? `${result.created[0]?.spotLabel} ${verb}`
          : `${result.created.length} Stellplätze ${verb}`,
      );
      onDone();
    },
    onError: async (error) => {
      // Die Absage nennt, was im Weg steht – als deutscher Satz mit Code, nie als Entwicklertext;
      // sie bleibt im Dialog stehen, der Toast verschwindet.
      const apiError = await toApiError(error);
      const text = parkingChangeProblem(
        apiError,
        'Die Belegung wurde nicht gespeichert.',
        'gespeichert wurde',
      );
      setProblem(text);
      toast.error(text);
      await qc.invalidateQueries({ queryKey: ['parking-occupancy', propertyGroupId] });
    },
  });

  const invalid =
    spotIds.length === 0 ||
    !draft.from ||
    !draft.to ||
    draft.from >= draft.to ||
    (draft.mode === 'guest' && !draft.bookingId);

  return {
    draft,
    patch,
    create,
    invalid,
    rejected,
    problem,
    clearRejected: () => setRejected([]),
  };
}
